import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { useSubscription } from '../hooks/subscription/use-subscription';
import { getLocale } from '../utils/get-locale';

/** «59», «47,20»: the banner copy appends the currency itself. */
function formatChargeAmount(amountCents: number): string {
  const amount = amountCents / 100;
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  return amount.toLocaleString(getLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: false,
  });
}

export function TrialEndingBanner() {
  const { t } = useTranslation('trialEndingBanner');
  const {
    isTrialExpiringSoon,
    subscription,
    billingInterval,
    billingViewState,
    nextCharge,
    planModulesConfig,
  } = useSubscription();
  const { pathname } = useLocation();

  if (!isTrialExpiringSoon) {
    return null;
  }

  if (pathname === '/settings/billing') {
    return null;
  }

  const trialEndsAt = subscription?.trialEndsAt;
  const dateStr = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '-';

  const isTrialNoCommitment = billingViewState === 'TRIAL_NO_COMMITMENT';
  const isAnnual = billingInterval === 'ANNUAL';
  // The first charge as the server computes it, discount included; the list
  // price only while an older response lacks it.
  const listPrice = planModulesConfig?.PROFESSIONAL?.[isAnnual ? 'annualPrice' : 'monthlyPrice'];
  const chargeCents = nextCharge?.amountCents ?? (listPrice == null ? null : listPrice * 100);

  const message =
    isTrialNoCommitment || chargeCents === null
      ? t('messageNoCommitment', { date: dateStr })
      : t('message', {
          date: dateStr,
          amount: t(isAnnual ? 'priceYearly' : 'priceMonthly', {
            price: formatChargeAmount(chargeCents),
          }),
        });

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberIcon />}
      sx={{ mb: 2, borderRadius: 0 }}
      action={
        <Button component={Link} to="/settings/billing" size="small" color="inherit">
          {isTrialNoCommitment ? t('addPayment') : t('manage')}
        </Button>
      }
    >
      {message}
    </Alert>
  );
}
